import {
    differenceInCalendarDays,
    differenceInSeconds,
    Duration,
    formatDistanceStrict,
    formatISODuration,
    Interval,
    intervalToDuration,
    subSeconds,
} from 'date-fns';

/**
 * Returns a time range from the current date to a date `timeInSeconds` in the past
 * ex. Where now is '12/20/20 3:30PM', getIntervalFromDate(86400) returns {start: '12/19/20 3:30PM', end: '12/20/20 3:30PM'}
 * @param timeInSeconds the total duration of the time range in seconds
 * @returns an Interval representing the start and end of the time range of length `timeInSeconds`
 */
export const getIntervalFromNow = (timeInSeconds: number): Interval => {
    const now = new Date();
    return { start: subSeconds(now, timeInSeconds), end: now };
};

/**
 * Gets a duration object given a timeInSeconds
 * @endDate the end date of the interval
 * @param timeInSeconds the total duration of the time range in seconds
 * @returns a Duration object representing the length of the given time range
 * split into years, months, days, etc. per ISO standard
 */
export const getDuration = (timeInSeconds: number): Duration =>
    intervalToDuration(getIntervalFromNow(timeInSeconds));

/**
 * Given the total time in seconds, returns an ISO 8061 formatted duration string
 * @param timeInSeconds The total duration of the time range in seconds
 * @returns an ISO 8061 formatted duration string
 * @example getISODuration(86525) => 'P1DT2M5S'
 */
export const getISODuration = (timeInSeconds: number) =>
    formatISODuration(getDuration(timeInSeconds));

/**
 * Formats a date relative to a supplied `fromDate`.  If the `fromDate`
 * is not supplied, it will default to now.
 *
 * Examples:
 *
 * * "2 years ago"
 * * "1 year ago"
 * * "2 months ago"
 * * "1 month ago"
 * * "2 days ago"
 * * "yesterday"
 * * "2 hours ago"
 * * "1 hour ago"
 * * "2 minutes ago"
 * * "1 minute ago"
 * * "a few seconds ago"
 * * "now"
 * * "in a few seconds"
 * * "in 1 minute"
 * * "in 2 minutes"
 * * "in 1 hour"
 * * "in 2 hours"
 * * "tomorrow"
 * * "in 2 days"
 * * "in 1 month"
 * * "in 2 months"
 * * "in 1 year"
 * * "in 2 years"
 *
 * @param date the `date` to format relative to `fromDate`.
 * @param fromDate the relative date to compare the supplied `date` to (defaults to now).
 */
export const formatDistanceFrom = (date: Date | null | undefined, fromDate?: Date) => {
    if (!date) {
        return 'unknown';
    }

    const from = fromDate ?? new Date();

    const secondsDiff = differenceInSeconds(date, from);
    if (secondsDiff === 0) {
        return 'now';
    }
    if (secondsDiff < 0 && secondsDiff > -60) {
        return 'a few seconds ago';
    }
    if (secondsDiff > 0 && secondsDiff < 60) {
        return 'in a few seconds';
    }

    const daysDiff = differenceInCalendarDays(date, from);
    if (daysDiff === -1) {
        return 'yesterday';
    }
    if (daysDiff === 1) {
        return 'tomorrow';
    }

    return formatDistanceStrict(date, from, {
        addSuffix: true,
    });
};
